import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMsg, resolveImage, formatINR } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useConfig } from "../lib/ConfigContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  User as UserIcon, Camera, Save, Crown, ShieldCheck, Building2,
  Hammer, Heart, Wallet, Mail, Phone, BadgeCheck, KeyRound, LogOut,
} from "lucide-react";
import usePageTitle from "../lib/usePageTitle";
import PageHeader from "../shared/components/PageHeader";

/**
 * v2.35 — PART 16: Unified profile system for ALL roles (admin, dealer, builder,
 * client, team). Picture upload via /api/upload, name/phone/bio editable via
 * PATCH /api/auth/profile, plan/subscription details visible inline.
 */
const ROLE_META = {
  admin: { label: "Admin", Icon: ShieldCheck, accent: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]" },
  team: { label: "Team Member", Icon: BadgeCheck, accent: "bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]" },
  dealer: { label: "Dealer", Icon: Building2, accent: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
  builder: { label: "Builder / Owner", Icon: Hammer, accent: "bg-[#F5F3FF] to-white text-[#6D28D9] border-[#DDD6FE]" },
  client: { label: "Client", Icon: UserIcon, accent: "bg-[#EEF4FF] text-[#0055FF] border-[#C7D9FF]" },
};

export default function Profile() {
  const { user, refreshUser, tier, logout } = useAuth();
  const { settings } = useConfig();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedCount, setSavedCount] = useState(null);
  const [commission, setCommission] = useState(null);
  // v2.54 — Phase C Part 5: Email-OTP modal for sensitive field changes
  const [otpModal, setOtpModal] = useState(null);  // { field, new_value, masked_email }
  const [otpInput, setOtpInput] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const fileRef = useRef();

  usePageTitle("My Profile | HouseEntry");

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setPhone(user.phone || "");
    setEmail(user.email || "");
    setBio(user.bio || "");
    setProfileImage(user.profile_image || "");
  }, [user?.id]);

  // Side-data: saved count for clients, commission summary for dealers
  useEffect(() => {
    if (!user) return;
    if (user.role === "client") {
      api.get("/me/saved").then((r) => setSavedCount(r.data?.total ?? 0)).catch(() => {});
    }
    if (user.role === "dealer") {
      api.get("/dealer/commission").then((r) => setCommission(r.data)).catch(() => {});
    }
  }, [user?.id, user?.role]);

  if (!user) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 bg-[#FAFAFA]">
        <div className="max-w-md w-full bg-white border border-[#E4E4E7] rounded-sm p-10 text-center" data-testid="profile-anon">
          <UserIcon size={28} className="mx-auto text-[#52525B] mb-3" />
          <h1 className="font-heading font-bold text-2xl tracking-tight">Sign in to view your profile</h1>
          <Link to="/login?next=/profile" className="inline-block mt-5">
            <Button className="btn-brand rounded-sm" data-testid="profile-login-cta">Sign in</Button>
          </Link>
        </div>
      </div>
    );
  }

  const meta = ROLE_META[user.role] || ROLE_META.client;
  const RoleIcon = meta.Icon;

  const onPickPhoto = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large — max 10 MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      // Reuse existing /api/upload pipeline (admins/dealers/builders allowed).
      // Clients aren't allowed by /api/upload yet — surface a clear error if so.
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfileImage(r.data.url);
      // Auto-save the picture as soon as it's uploaded so users don't lose it
      // by forgetting to hit Save.
      await api.patch("/auth/profile", { profile_image: r.data.url });
      await refreshUser?.();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't upload photo"));
    }
    e.target.value = "";
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      // v2.58 — OTP requirement removed per user request. Email + Phone changes
      // save directly along with name + bio.
      await api.patch("/auth/profile", { name, bio, email, phone });
      await refreshUser?.();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't save profile"));
    }
    setBusy(false);
  };

  // v2.54 — Phase C Part 5: trigger OTP flow for email/phone change
  const requestOtp = async (field, new_value) => {
    try {
      const r = await api.post("/auth/profile/request-otp", { field, new_value });
      setOtpModal({ field, new_value, masked: r.data?.to_email_masked || user.email });
      setOtpInput("");
      toast.success(`OTP sent to ${r.data?.to_email_masked || user.email}`);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't send OTP"));
    }
  };

  const verifyOtp = async () => {
    if (!otpModal) return;
    if (!/^\d{6}$/.test(otpInput.trim())) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setOtpBusy(true);
    try {
      await api.post("/auth/profile/verify-otp", {
        field: otpModal.field,
        new_value: otpModal.new_value,
        otp: otpInput.trim(),
      });
      toast.success(otpModal.field === "email" ? "Email updated" : "Phone updated");
      setOtpModal(null);
      setOtpInput("");
      await refreshUser?.();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Verification failed"));
    }
    setOtpBusy(false);
  };

  const cancelOtp = () => {
    setOtpModal(null);
    setOtpInput("");
    // Reset draft values to current saved ones so user isn't stuck
    setEmail(user.email || "");
    setPhone(user.phone || "");
  };

  const sub = user.subscription || {};
  const planActive = !!sub.active && !!sub.tier;
  const planLabel = planActive ? sub.tier.charAt(0).toUpperCase() + sub.tier.slice(1) : "No active plan";
  const planExpiresLabel = planActive && sub.expires_at
    ? new Date(sub.expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* v2.71.51 — Integrated header replaces the floating Back pill. */}
      <div className="sticky top-0 z-30 bg-white border-b border-[#E4E4E7]">
        <div className="max-w-[960px] mx-auto px-4 sm:px-6 lg:px-10">
          <PageHeader title="Profile & account" backLabel="Home" home="/" borderless />
        </div>
      </div>
      <div className="max-w-[960px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#0055FF] mb-2 flex items-center gap-2">
          <UserIcon size={12} /> My Profile
        </div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090B] mb-1">
          {user.name || user.email}
        </h1>
        <p className="text-[13px] text-[#52525B] mb-8">
          Manage your details, profile photo and subscription in one place.
        </p>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
          {/* LEFT: photo + role badge + side metrics */}
          <div className="space-y-4">
            <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 text-center">
              <div className="relative w-32 h-32 mx-auto rounded-full bg-[#F4F4F5] overflow-hidden border border-[#E4E4E7]" data-testid="profile-photo">
                {profileImage ? (
                  <img src={resolveImage(profileImage)} alt="Profile" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#A1A1AA]">
                    <UserIcon size={48} strokeWidth={1.5} />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                  className="absolute bottom-1 right-1 w-9 h-9 bg-[#0055FF] text-white rounded-full flex items-center justify-center shadow-md hover:bg-[#003ED6] disabled:opacity-50"
                  data-testid="profile-photo-btn"
                  aria-label="Change profile photo"
                >
                  <Camera size={14} />
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} />
              </div>
              <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 mt-4 rounded-sm border ${meta.accent}`} data-testid="profile-role-badge">
                <RoleIcon size={11} /> {meta.label}
              </div>
              <div className="mt-2 text-[13px] text-[#52525B] flex items-center justify-center gap-1.5">
                <Mail size={11} /> {user.email}
              </div>
              {user.phone && (
                <div className="mt-1 text-[13px] text-[#52525B] flex items-center justify-center gap-1.5">
                  <Phone size={11} /> {user.phone}
                </div>
              )}
            </div>

            {/* Plan summary (clients) — admins/team always premium-equivalent */}
            <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="profile-plan-card">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] flex items-center gap-1.5 mb-2">
                <Crown size={11} /> Subscription
              </div>
              <div className="font-heading font-bold text-xl tracking-tight" data-testid="profile-plan-label">
                {user.role === "admin" || user.role === "team" ? "Admin Access (all features)" : planLabel}
              </div>
              {planExpiresLabel && user.role !== "admin" && user.role !== "team" && (
                <div className="text-[12px] text-[#52525B] mt-0.5">Renews on {planExpiresLabel}</div>
              )}
              {!planActive && user.role === "client" && (
                <Link to="/pricing">
                  <Button size="sm" className="btn-brand rounded-sm mt-3" data-testid="profile-upgrade-btn">
                    <Crown size={12} className="mr-1.5" /> See plans
                  </Button>
                </Link>
              )}
            </div>

            {/* Role-specific metrics */}
            {user.role === "client" && (
              <Link to="/saved" className="block bg-white border border-[#E4E4E7] rounded-sm p-5 hover:bg-[#FAFAFA]" data-testid="profile-saved-link">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#DC2626] flex items-center gap-1.5">
                  <Heart size={11} fill="currentColor" /> Saved Properties
                </div>
                <div className="font-heading font-bold text-2xl tracking-tight mt-1">
                  {savedCount === null ? "…" : savedCount}
                </div>
                <div className="text-[11px] text-[#52525B] mt-0.5">View your collection →</div>
              </Link>
            )}

            {user.role === "dealer" && commission && (
              <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="profile-earnings-card">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#15803D] flex items-center gap-1.5">
                  <Wallet size={11} /> Earnings ({commission.share_pct ?? 50}% share)
                </div>
                <div className="font-heading font-bold text-2xl tracking-tight mt-1 font-mono-data">
                  {formatINR(commission.dealer_share || 0)}
                </div>
                <div className="text-[11px] text-[#52525B] mt-0.5">
                  {commission.sold_count} sold · {commission.share_pct_source === "custom" ? "Custom rate" : "Default rate"}
                </div>
              </div>
            )}

            {user.role === "builder" && (
              <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6D28D9]">Builder / Owner workspace</div>
                <div className="text-[13px] text-[#52525B] mt-2">
                  You own your inventory — projects you publish go live after admin review.
                </div>
                <Link to="/builder?tab=projects" className="inline-block mt-3">
                  <Button size="sm" variant="outline" className="rounded-sm" data-testid="profile-builder-projects">Manage Projects</Button>
                </Link>
              </div>
            )}
          </div>

          {/* RIGHT: edit form */}
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 space-y-4" data-testid="profile-edit-form">
            <h2 className="font-heading font-semibold text-xl tracking-tight">Edit Details</h2>

            <Field label="Full name" required>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="profile-name-input" />
            </Field>

            <Field
              label="Phone"
              hint={(user.role === "dealer" || user.role === "builder") ? "Required (used by clients to contact you for callbacks)" : "Optional"}
            >
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="rounded-sm"
                placeholder="+91 98xxxxxxxx"
                data-testid="profile-phone-input"
              />
            </Field>

            <Field label="Bio / About" hint={user.role === "dealer" ? "Shown on your verified-partner badge (max 500 chars)" : "Tell us a bit about yourself (max 500 chars)"}>
              <Textarea
                rows={4}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="rounded-sm"
                maxLength={500}
                data-testid="profile-bio-input"
              />
              <div className="text-[10px] text-[#A1A1AA] mt-1 text-right">{bio.length}/500</div>
            </Field>

            <Field label="Email" hint="You can update your email here. Make sure it's correct.">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm"
                placeholder="you@example.com"
                data-testid="profile-email-input"
              />
            </Field>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#F4F4F5] flex-wrap">
              <Button
                variant="outline"
                onClick={() => {
                  if (window.confirm("Logout from HouseEntry?")) logout?.();
                }}
                className="rounded-sm text-[#DC2626] border-[#FECACA] hover:bg-[#FEE2E2]"
                data-testid="profile-logout-btn"
              >
                <LogOut size={13} className="mr-1.5" /> Logout
              </Button>
              <Button onClick={save} disabled={busy} className="btn-brand rounded-sm" data-testid="profile-save-btn">
                <Save size={13} className="mr-1.5" /> {busy ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* v2.54 — Phase C Part 5: OTP verification modal for email / phone change */}
      <Dialog open={!!otpModal} onOpenChange={(v) => { if (!v) cancelOtp(); }}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="profile-otp-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading tracking-tight">
              <KeyRound size={16} className="text-[#0055FF]" />
              Verify {otpModal?.field === "email" ? "Email Change" : "Phone Change"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-[12.5px] text-[#52525B] space-y-2 mb-4">
            <p>
              Aapne <b>{otpModal?.field === "email" ? "email" : "phone"}</b> ko update karne ki request ki hai:
            </p>
            <p className="font-mono-data text-[#09090B]">→ {otpModal?.new_value}</p>
            <p>
              Ek 6-digit verification code <b>{otpModal?.masked}</b> par bheja gaya hai. Code daal kar confirm karein.
            </p>
          </div>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit code"
            value={otpInput}
            onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="rounded-sm font-mono-data text-center text-lg tracking-widest"
            data-testid="profile-otp-input"
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={cancelOtp} disabled={otpBusy} className="rounded-sm" data-testid="profile-otp-cancel">
              Cancel
            </Button>
            <Button
              onClick={verifyOtp}
              disabled={otpBusy || otpInput.length !== 6}
              className="btn-brand rounded-sm"
              data-testid="profile-otp-verify"
            >
              {otpBusy ? "Verifying…" : "Verify & Save"}
            </Button>
          </div>
          <button
            type="button"
            onClick={() => requestOtp(otpModal.field, otpModal.new_value)}
            disabled={otpBusy}
            className="w-full text-center text-[11.5px] text-[#0055FF] hover:underline mt-3 disabled:opacity-50"
            data-testid="profile-otp-resend"
          >
            Resend code
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, required, hint }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1.5 flex items-center gap-1.5">
      {label}
      {required && <span className="text-[9px] font-normal text-[#DC2626] normal-case tracking-normal">*</span>}
    </div>
    {children}
    {hint && <div className="text-[11px] text-[#A1A1AA] mt-1">{hint}</div>}
  </div>
);
